import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// SusTek brand colours
const BRAND = { dark: [26, 32, 21], olive: [136, 136, 100], beige: [247, 247, 246], accent: [69, 83, 50] };

function fmt(n) { return n != null ? Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '-'; }
function fmtEur(n) { return n != null ? `EUR ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '-'; }

function addHeader(pdf, title, subtitle) {
  const w = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(...BRAND.dark);
  pdf.rect(0, 0, w, 22, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title, 10, 10);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...BRAND.olive);
  pdf.text(subtitle, 10, 17);
  pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), w - 10, 17, { align: 'right' });
}

function addPageHeader(pdf, text) {
  const w = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(...BRAND.dark);
  pdf.rect(0, 0, w, 14, 'F');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...BRAND.olive);
  pdf.text(text, 10, 9);
}

function addFooter(pdf) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  const pages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(7);
    pdf.setTextColor(...BRAND.olive);
    pdf.text('CONFIDENTIAL — SusTek EESG Control Tower — CBAM Declaration Pack', 10, h - 5);
    pdf.text(`Page ${i} of ${pages}`, w - 10, h - 5, { align: 'right' });
  }
}

function sectionTitle(pdf, y, title) {
  const w = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(...BRAND.accent);
  pdf.rect(10, y, w - 20, 8, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title, 14, y + 5.5);
  return y + 12;
}

function labelValue(pdf, x, y, label, value, valueWidth) {
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...BRAND.olive);
  pdf.text(label, x, y);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...BRAND.dark);
  pdf.text(String(value), x + (valueWidth || 55), y);
}

export async function exportCbamDeclarationPack(apiCall, period) {
  // Show overlay
  const overlay = document.createElement('div');
  overlay.id = 'pdf-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(26,32,21,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
  overlay.innerHTML = '<div style="background:white;padding:24px 40px;border-radius:12px;text-align:center;font-family:Manrope,sans-serif"><p style="font-size:14px;font-weight:600;color:#1A2015">Generating CBAM Declaration Pack...</p><p style="font-size:12px;color:#888864;margin-top:4px">Compiling shipments, emissions, and evidence</p></div>';
  document.body.appendChild(overlay);

  try {
    const res = await apiCall(period);
    const data = res.data;
    const { declaration: decl, shipments, product_summary: products, supplier_evidence: suppliers, installations, carbon_prices, generated_by } = data;

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const w = pdf.internal.pageSize.getWidth();
    const periodLabel = decl.period;

    // ───── COVER PAGE ─────
    pdf.setFillColor(...BRAND.dark);
    pdf.rect(0, 0, w, 297, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(28);
    pdf.setTextColor(255, 255, 255);
    pdf.text('CBAM Declaration Pack', 20, 60);

    pdf.setFontSize(14);
    pdf.setTextColor(...BRAND.olive);
    pdf.text(`${periodLabel} — Manufacturing APAC Group`, 20, 72);

    pdf.setDrawColor(136, 136, 100);
    pdf.setLineWidth(0.5);
    pdf.line(20, 80, 120, 80);

    let cy = 95;
    const coverItems = [
      ['Reporting Entity', 'SusTek Manufacturing Group'],
      ['Scope', 'APAC Manufacturing — Plants A, B, C'],
      ['Period', `${decl.period_start} to ${decl.period_end}`],
      ['Submission Deadline', decl.deadline],
      ['Status', decl.status],
      ['Total Shipments', String(decl.total_shipments)],
      ['Total Embedded Emissions', `${fmt(decl.total_embedded_tco2e)} tCO2e`],
      ['Gross CBAM Liability', fmtEur(decl.gross_cbam_eur)],
      ['Carbon Price Deductions', fmtEur(decl.total_deductions_eur)],
      ['Net CBAM Liability', fmtEur(decl.net_liability_eur)],
      ['Declaration Readiness', `${decl.declaration_readiness_pct}%`],
      ['Generated By', generated_by],
    ];
    for (const [label, value] of coverItems) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(...BRAND.olive);
      pdf.text(label, 20, cy);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.text(value, 90, cy);
      cy += 8;
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(100, 100, 80);
    pdf.text('Prepared in accordance with EU Regulation 2023/956 (CBAM) and Implementing Regulation 2023/1773', 20, 260);
    pdf.text('Annex III — Rules for calculating embedded emissions of goods', 20, 265);

    // ───── SECTION 1: PRODUCT & EMISSIONS REGISTER ─────
    pdf.addPage();
    addHeader(pdf, 'Section 1: Product & Emissions Register', `CBAM Annex III — ${periodLabel}`);
    let y = 30;
    y = sectionTitle(pdf, y, '1.1 Product Summary');

    autoTable(pdf, {
      startY: y,
      margin: { left: 10, right: 10 },
      head: [['Product', 'CN Code', 'Sector', 'Route', 'Tonnes', 'tCO2e/t', 'Total tCO2e', 'Gross EUR', 'Deductions', 'Net EUR']],
      body: products.map(p => [
        p.description?.substring(0, 25) || '',
        p.cn_code,
        p.sector,
        p.route_code,
        fmt(p.total_tonnes),
        fmt(p.total_intensity),
        fmt(p.total_embedded_tco2e),
        fmtEur(p.total_gross_eur),
        fmtEur(p.total_deductions_eur),
        fmtEur(p.total_net_eur),
      ]),
      styles: { fontSize: 7, cellPadding: 1.5, textColor: BRAND.dark, lineColor: [200, 200, 190] },
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [245, 245, 240] },
    });

    y = pdf.lastAutoTable.finalY + 8;
    if (y > 240) { pdf.addPage(); addPageHeader(pdf, `CBAM Declaration Pack — ${periodLabel}`); y = 20; }
    y = sectionTitle(pdf, y, '1.2 Emissions Intensity Detail (Annex III Methodology)');

    autoTable(pdf, {
      startY: y,
      margin: { left: 10, right: 10 },
      head: [['Product', 'Direct (Scope 1)', 'Indirect (Scope 2)', 'Precursor', 'Total', 'Sector Default', 'vs Default', 'Verified', 'Method']],
      body: products.map(p => [
        p.description?.substring(0, 22) || '',
        `${fmt(p.direct_tco2e_per_t)} tCO2e/t`,
        `${fmt(p.indirect_tco2e_per_t)} tCO2e/t`,
        `${fmt(p.precursor_tco2e_per_t)} tCO2e/t`,
        `${fmt(p.total_intensity)} tCO2e/t`,
        `${fmt(p.sector_default_intensity)} tCO2e/t`,
        `${p.total_intensity < p.sector_default_intensity ? '' : '+'}${(((p.total_intensity - p.sector_default_intensity) / (p.sector_default_intensity || 1)) * 100).toFixed(1)}%`,
        p.verified_flag ? 'YES' : 'NO',
        p.methodology || '',
      ]),
      styles: { fontSize: 7, cellPadding: 1.5, textColor: BRAND.dark, lineColor: [200, 200, 190] },
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 },
      alternateRowStyles: { fillColor: [245, 245, 240] },
    });

    // ───── SECTION 2: SHIPMENT DETAIL ─────
    pdf.addPage();
    addHeader(pdf, 'Section 2: Shipment-by-Shipment Detail', `${periodLabel} — EU-bound consignments`);
    y = 30;
    y = sectionTitle(pdf, y, '2.1 All Declared Shipments');

    autoTable(pdf, {
      startY: y,
      margin: { left: 10, right: 10 },
      head: [['ID', 'Product', 'Qty (t)', 'Dest.', 'Importer', 'Embedded', 'Intensity', 'Gross', 'Deduction', 'Net', 'Evidence', 'Status']],
      body: shipments.map(s => [
        s.shipment_id,
        s.product_desc?.substring(0, 16) || '',
        fmt(s.quantity_tonnes),
        s.destination_country,
        s.importer?.substring(0, 18) || '',
        `${fmt(s.embedded_total_tco2e)} t`,
        fmt(s.embedded_intensity),
        fmtEur(s.gross_cbam_eur),
        fmtEur(s.carbon_deduction_eur),
        fmtEur(s.net_cbam_eur),
        `${s.evidence_completeness_pct}%`,
        s.status,
      ]),
      styles: { fontSize: 6, cellPadding: 1.2, textColor: BRAND.dark, lineColor: [200, 200, 190] },
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6 },
      alternateRowStyles: { fillColor: [245, 245, 240] },
      didParseCell: (data) => {
        if (data.column.index === 11 && data.section === 'body') {
          const val = data.cell.raw;
          if (val === 'Actual Verified') data.cell.styles.textColor = [22, 163, 74];
          else if (val === 'Default Applied' || val === 'Non-Compliant') data.cell.styles.textColor = [220, 38, 38];
          else if (val === 'Pending Review' || val === 'Actual Unverified') data.cell.styles.textColor = [217, 119, 6];
        }
      },
    });

    // ───── SECTION 3: SUPPLIER EVIDENCE & CARBON DEDUCTIONS ─────
    pdf.addPage();
    addHeader(pdf, 'Section 3: Supplier Evidence & Carbon Deductions', `Precursor data — ${periodLabel}`);
    y = 30;
    y = sectionTitle(pdf, y, '3.1 Precursor Supplier Register');

    autoTable(pdf, {
      startY: y,
      margin: { left: 10, right: 10 },
      head: [['Supplier', 'Precursor', 'Country', 'Sharing', 'Verification', 'Intensity', 'C-Price', 'Completeness', 'Last Submit', 'Validity']],
      body: suppliers.map(s => [
        s.name,
        s.precursor_type,
        s.country,
        s.data_sharing_status,
        s.verification_status,
        `${fmt(s.embedded_intensity_tco2e_t)} tCO2e/t`,
        s.carbon_price_eur_tco2e > 0 ? `EUR ${fmt(s.carbon_price_eur_tco2e)}` : 'None',
        `${s.data_completeness_pct}%`,
        s.last_submission ? new Date(s.last_submission).toLocaleDateString() : 'Never',
        s.validity_period || 'N/A',
      ]),
      styles: { fontSize: 6.5, cellPadding: 1.5, textColor: BRAND.dark, lineColor: [200, 200, 190] },
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 },
      alternateRowStyles: { fillColor: [245, 245, 240] },
    });

    y = pdf.lastAutoTable.finalY + 8;
    y = sectionTitle(pdf, y, '3.2 Carbon Price Deductions (Article 9)');

    autoTable(pdf, {
      startY: y,
      margin: { left: 10, right: 10 },
      head: [['Jurisdiction', 'Mechanism', 'Price (EUR/tCO2e)', 'Effective Date', 'Notes']],
      body: carbon_prices.map(cp => [
        cp.jurisdiction,
        cp.mechanism,
        `EUR ${fmt(cp.price_eur_tco2e)}`,
        cp.effective_date,
        cp.notes,
      ]),
      styles: { fontSize: 7, cellPadding: 2, textColor: BRAND.dark, lineColor: [200, 200, 190] },
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [245, 245, 240] },
    });

    // ───── SECTION 4: INSTALLATION DATA & VERIFICATION ─────
    y = pdf.lastAutoTable.finalY + 8;
    if (y > 210) { pdf.addPage(); addPageHeader(pdf, `CBAM Declaration Pack — ${periodLabel}`); y = 20; }
    y = sectionTitle(pdf, y, '4.1 Installation Register');

    autoTable(pdf, {
      startY: y,
      margin: { left: 10, right: 10 },
      head: [['ID', 'Site', 'Country', 'Operator', 'Direct tCO2e', 'Indirect tCO2e', 'Grid Factor', 'Verifier', 'Status']],
      body: installations.map(inst => [
        inst.installation_id,
        inst.site_name,
        `${inst.country} (${inst.country_code})`,
        inst.operator_entity,
        fmt(inst.direct_emissions_tco2e),
        fmt(inst.indirect_emissions_tco2e),
        `${inst.grid_factor_tco2e_mwh} tCO2e/MWh`,
        inst.verifier,
        inst.verification_status,
      ]),
      styles: { fontSize: 7, cellPadding: 1.5, textColor: BRAND.dark, lineColor: [200, 200, 190] },
      headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [245, 245, 240] },
    });

    y = pdf.lastAutoTable.finalY + 6;
    for (const inst of installations) {
      if (y > 255) { pdf.addPage(); addPageHeader(pdf, `CBAM Declaration Pack — ${periodLabel}`); y = 20; }
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(...BRAND.dark);
      pdf.text(`${inst.site_name} — Fuel Mix`, 14, y);
      y += 4;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(...BRAND.olive);
      const fuelEntries = Object.entries(inst.fuel_mix || {});
      for (const [fuel, pct] of fuelEntries) {
        pdf.text(`${fuel}: ${pct}%`, 18, y);
        y += 3.5;
      }
      pdf.text(`Production routes: ${(inst.production_routes || []).join(', ')}`, 18, y);
      y += 6;
    }

    // ───── FOOTER ON ALL PAGES ─────
    addFooter(pdf);

    pdf.save(`SusTek_CBAM_Declaration_${periodLabel.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error('CBAM Declaration Pack export failed:', err);
    alert(`Export failed: ${err.response?.data?.detail || err.message}`);
  } finally {
    const o = document.getElementById('pdf-overlay');
    if (o) o.remove();
  }
}
